/**
 * Dynamic field renderer for form response detail view
 * Handles different field types: text, email, phone, textarea, number, date, etc.
 */

import React from 'react';
import { Mail, Phone, MapPin, Link as LinkIcon } from 'lucide-react';
import { renderFieldValue } from '../utils/formResponseFormatters';

const FormResponseField = ({
    id,
    label,
    value,
    type = 'text',
    isMetadata = false,
}) => {
    // Handle empty values
    const isEmpty = value === null || value === undefined || value === '';

    return (
        <div className="space-y-2">
            <label className={`text-xs font-semibold ${isMetadata ? 'text-stone-500' : 'text-stone-600'} uppercase tracking-wider`}>
                {label}
            </label>
            <div className={`${isMetadata ? 'bg-stone-50' : 'bg-white'} border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-700`}>
                {isEmpty ? (
                    <span className="text-stone-400 italic">Not provided</span>
                ) : type === 'email' ? (
                    <a href={`mailto:${value}`} className="text-blue-600 hover:underline flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5" />
                        {value}
                    </a>
                ) : type === 'phone' ? (
                    <a href={`tel:${value}`} className="text-blue-600 hover:underline flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5" />
                        {value}
                    </a>
                ) : type === 'url' ? (
                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5" />
                        <span className="truncate">{value}</span>
                    </a>
                ) : type === 'textarea' ? (
                    <div className="whitespace-pre-wrap break-words bg-stone-50 p-3 rounded border border-stone-200 text-xs">
                        {value}
                    </div>
                ) : type === 'checkbox' || type === 'boolean' ? (
                    <span className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-medium ${value ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-stone-100 text-stone-600 border border-stone-200'
                        }`}>
                        {value ? '✓ Yes' : '✗ No'}
                    </span>
                ) : type === 'date' ? (
                    <span className="flex items-center gap-2">
                        {new Date(value).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </span>
                ) : Array.isArray(value) ? (
                    <div className="flex flex-wrap gap-1">
                        {value.map((v, i) => (
                            <span key={i} className="inline-block px-2 py-0.5 bg-stone-100 border border-stone-200 rounded text-xs text-stone-700">
                                {String(v)}
                            </span>
                        ))}
                    </div>
                ) : (
                    String(value)
                )}
            </div>
        </div>
    );
};

export default FormResponseField;
